class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    const cleanupPromise = window.cleanupSubscriptionItems ? 
      window.cleanupSubscriptionItems() : 
      Promise.resolve();
    
    cleanupPromise.then(() => {
      fetch('/cart?section_id=cart-drawer')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newDrawerInner = html.querySelector('.drawer__inner');
        const currentDrawerInner = this.querySelector('.drawer__inner');
        
        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          
          const hasEmptyContent = newDrawerInner.querySelector('.drawer__inner-empty');
          if (hasEmptyContent) {
            this.classList.add('is-empty');
          } else {
            this.classList.remove('is-empty');
          }
        }
        
        // 🔥 bubble 업데이트 (innerHTML 교체 없이 함수만 호출)
        if (typeof window.updateCartBubble === 'function') {
          window.updateCartBubble();
        }
      })
      .catch((e) => {
        console.error('Cart refresh error:', e);
      });

      setTimeout(() => {
        this.classList.add('animate', 'active');
        const cartDrawerItems = this.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.updateCartUpsellToggleState();
          cartDrawerItems.updateCartUpsellVisibility();
        }
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble') {
        // 🔥 cart-icon-bubble: 스크립트 제외하고 bubble 내용만 업데이트
        const newHTML = parsedState.sections[section.id];
        const tempDoc = new DOMParser().parseFromString(newHTML, 'text/html');
        const newBubble = tempDoc.querySelector('.cart-count-bubble');
        const currentBubble = document.querySelector('.cart-count-bubble');
        
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      } else {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
    
    // 🔥 renderContents 후 bubble 업데이트
    setTimeout(() => {
      if (typeof window.updateCartBubble === 'function') {
        window.updateCartBubble();
      }
    }, 10);

    setTimeout(() => {
      if (!this.classList.contains('active')) {
        this.classList.add('animate', 'active');
        
        const cartDrawerItems = this.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.updateCartUpsellToggleState();
          cartDrawerItems.updateCartUpsellVisibility();
        }
        
        document.body.classList.add('overflow-hidden');
        
        this.addEventListener(
          'transitionend',
          () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
        );
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        if (parsedState.item_count === 0 && cartDrawerWrapper) {
          fetch(`${routes.cart_url}?section_id=cart-drawer`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const newDrawerInner = html.querySelector('.drawer__inner');
              const currentDrawerInner = cartDrawerWrapper.querySelector('.drawer__inner');
              
              if (newDrawerInner && currentDrawerInner) {
                currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
              }
              
              if (typeof window.updateCartBubble === 'function') {
                window.updateCartBubble();
              }
            })
            .catch((e) => {
              console.error('Empty cart reload error:', e);
            });
        } else {
          this.getSectionsToRender().forEach((section) => {
            if (section.id === 'CartDrawer') {
              const newHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = newHTML;

              const newCartItems = tempDiv.querySelector('.drawer__contents');
              const currentCartItems = document.querySelector('.drawer__contents');
              if (newCartItems && currentCartItems) {
                currentCartItems.innerHTML = newCartItems.innerHTML;
              }

              const newFooter = tempDiv.querySelector('.drawer__footer');
              const currentFooter = document.querySelector('.drawer__footer');
              if (newFooter && currentFooter) {
                const upsellCheckbox = currentFooter.querySelector('.cart-upsell-toggle-container');
                if (upsellCheckbox) {
                  newFooter.querySelector('.cart-upsell-toggle-container')?.replaceWith(upsellCheckbox.cloneNode(true));
                }
                currentFooter.innerHTML = newFooter.innerHTML;
              }

              const newProgressBar = tempDiv.querySelector('#cart-progress-wrapper');
              const currentProgressBar = document.querySelector('#cart-progress-wrapper');
              if (newProgressBar && currentProgressBar) {
                const newBar = newProgressBar.querySelector('#cart-progress-bar');
                const currentBar = currentProgressBar.querySelector('#cart-progress-bar');
                const newMessage = newProgressBar.querySelector('.goal-message');
                const currentMessage = currentProgressBar.querySelector('.goal-message');
                
                if (newBar && currentBar) {
                  currentBar.style.width = newBar.style.width;
                }
                
                if (newMessage && currentMessage) {
                  currentMessage.innerHTML = newMessage.innerHTML;
                }
                
                if (newProgressBar.classList.contains('full')) {
                  currentProgressBar.classList.add('full');
                } else {
                  currentProgressBar.classList.remove('full');
                }
              } else if (!newProgressBar && currentProgressBar) {
                currentProgressBar.remove();
              } else if (newProgressBar && !currentProgressBar) {
                const cartDrawerItems = document.querySelector('cart-drawer-items');
                if (cartDrawerItems) {
                  cartDrawerItems.insertAdjacentElement('beforebegin', newProgressBar);
                }
              }
            } else if (section.id === 'cart-icon-bubble') {
              // 🔥 cart-icon-bubble: 스크립트 제외하고 bubble 내용만 업데이트
              const newHTML = parsedState.sections[section.section];
              const tempDoc = new DOMParser().parseFromString(newHTML, 'text/html');
              const newBubble = tempDoc.querySelector('.cart-count-bubble');
              const currentBubble = document.querySelector('.cart-count-bubble');
              
              if (newBubble && currentBubble) {
                currentBubble.innerHTML = newBubble.innerHTML;
              }
              
              // 🔥 즉시 bubble 업데이트
              setTimeout(() => {
                if (typeof window.updateCartBubble === 'function') {
                  window.updateCartBubble();
                }
              }, 10);
            }
          });
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        this.updateCartUpsellToggleState();
        this.updateCartUpsellVisibility(parsedState.item_count);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);